/**
 * Token state → sprites, pointer events → drop intents — render-exempt
 * files (test-spec §7.2). The cell decision is NOT made here: drops hand
 * the world-space point to the caller, which uses shared/grid snapping
 * (input.ts risk note in §7.2 — no reimplemented math).
 */

import {
  Container,
  Graphics,
  Text,
  type FederatedPointerEvent,
} from "pixi.js";
import { cellCenter, type RoomState, type Token } from "@asarimitsuha/table-core";

export interface TokenLayerHandlers {
  canDrag(tokenId: string): boolean;
  /** World-space drop point; the caller snaps and issues the command. */
  onDrop(tokenId: string, worldX: number, worldY: number): void;
  /** Any pointerdown on a token selects it (drag or not). */
  onSelect(tokenId: string): void;
}

export interface TokenLayer {
  container: Container;
  sync(state: RoomState): void;
}

const PLAYER_FILL = 0x2f88d4;
const GM_FILL = 0xd4a12e;

function buildSprite(token: Token, cellSizePx: number): Container {
  const sprite = new Container();
  const radius = cellSizePx * 0.4 * token.size;
  const circle = new Graphics()
    .circle(0, 0, radius)
    .fill(token.ownerSeatId ? PLAYER_FILL : GM_FILL)
    .stroke({ width: 2, color: 0xe8e6df });
  const label = new Text({
    text: token.name,
    style: { fontSize: 12, fill: 0xe8e6df, fontFamily: "system-ui" },
  });
  label.anchor.set(0.5, 0);
  label.position.set(0, radius + 2);
  sprite.addChild(circle, label);
  return sprite;
}

export function createTokenLayer(
  world: Container,
  handlers: TokenLayerHandlers,
): TokenLayer {
  const container = new Container();
  container.sortableChildren = true;
  world.addChild(container);

  const byId = new Map<string, Container>();
  let draggingId: string | null = null;

  function attachDrag(sprite: Container, tokenId: string): void {
    sprite.eventMode = "static";
    sprite.cursor = "pointer";
    sprite.on("pointerdown", (event: FederatedPointerEvent) => {
      event.stopPropagation(); // background pointerdown pans (viewport.ts)
      handlers.onSelect(tokenId);
      if (!handlers.canDrag(tokenId)) return;
      draggingId = tokenId;
      sprite.zIndex = 100;
    });
    sprite.on("globalpointermove", (event: FederatedPointerEvent) => {
      if (draggingId !== tokenId) return;
      const local = container.toLocal(event.global);
      sprite.position.set(local.x, local.y);
    });
    const finish = (event: FederatedPointerEvent) => {
      if (draggingId !== tokenId) return;
      draggingId = null;
      sprite.zIndex = 0;
      const local = container.toLocal(event.global);
      handlers.onDrop(tokenId, local.x, local.y);
    };
    sprite.on("pointerup", finish);
    sprite.on("pointerupoutside", finish);
  }

  function sync(state: RoomState): void {
    const config = { cellSizePx: state.map.cellSizePx, feetPerCell: state.map.feetPerCell };
    const seen = new Set<string>();
    for (const token of Object.values(state.tokens)) {
      seen.add(token.id);
      let sprite = byId.get(token.id);
      if (!sprite) {
        sprite = buildSprite(token, state.map.cellSizePx);
        attachDrag(sprite, token.id);
        byId.set(token.id, sprite);
        container.addChild(sprite);
      }
      if (draggingId !== token.id) {
        const center = cellCenter({ col: token.col, row: token.row }, config);
        sprite.position.set(center.x, center.y);
      }
    }
    for (const [id, sprite] of byId) {
      if (!seen.has(id)) {
        sprite.destroy({ children: true });
        byId.delete(id);
        if (draggingId === id) draggingId = null;
      }
    }
  }

  return { container, sync };
}
